import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { CameraIcon, SparklesIcon, UploadIcon, PaletteIcon, ImageIcon, DownloadIcon, ArrowRightIcon, ExternalLinkIcon, CheckCircle2Icon } from 'lucide-react';
export function AIPhotoshootPage() {
  usePageSEO({
    title: 'AI Photoshoot | Design Flow Digitals',
    description: 'Transform your product images with AI-powered photoshoots by Design Flow Digitals. Professional-quality product photography without expensive studio sessions.'
  });
  return <div className="w-full selection:bg-purple-500/30 selection:text-white" data-id="element-406">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-407">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-black pointer-events-none" data-id="element-408" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" data-id="element-409" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-410">
          <div data-id="element-411">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-purple-500/30 bg-purple-500/10" data-id="element-412">
              <CameraIcon className="w-4 h-4 text-purple-400" data-id="element-413" />
              <span className="text-sm font-medium text-purple-300" data-id="element-414">
                Live on Shopify App Store
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" data-id="element-415">
              AI Product Photoshoot
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-416">
              Transform your Shopify images into high-converting lifestyle
              scenes in seconds. No photoshoots required.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-417">
              <a href="https://apps.shopify.com/ai-product-photoshoot-dfd" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-200 transition-colors shadow-[0_0_30px_rgba(255,255,255,0.2)]" data-id="element-418">
                Install Free on Shopify <ExternalLinkIcon className="w-5 h-5" data-id="element-419" />
              </a>
              <a href="#how-it-works" className="px-8 py-4 rounded-full font-bold text-lg text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-420">
                Learn More
              </a>
            </div>
          </div>

          {/* Product Demo Mockup */}
          <div className="relative" data-id="element-421">
            <div className="absolute inset-0 bg-purple-500/20 blur-[100px] rounded-full" data-id="element-422" />
            <div className="glass-card border-white/10 p-2 rounded-2xl relative z-10 shadow-2xl shadow-black/50" data-id="element-423">
              <div className="bg-[#0A0A0A] rounded-xl overflow-hidden border border-white/5" data-id="element-424">
                {/* Fake App UI Header */}
                <div className="h-12 border-b border-white/10 flex items-center px-4 gap-2 bg-white/5" data-id="element-425">
                  <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-426" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-427" />
                  <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-428" />
                  <div className="ml-4 text-xs text-gray-500 font-medium" data-id="element-429">
                    AI Photoshoot Studio
                  </div>
                </div>

                {/* Mockup Content */}
                <div className="p-8" data-id="element-430">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-431">
                    {/* Before */}
                    <div className="flex-1 w-full bg-white/5 rounded-xl p-4 border border-white/10 text-center" data-id="element-432">
                      <div className="aspect-square bg-white/10 rounded-lg mb-4 flex items-center justify-center" data-id="element-433">
                        <CameraIcon className="w-12 h-12 text-gray-500" data-id="element-434" />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-400" data-id="element-435">
                        Before (Plain)
                      </span>
                    </div>

                    {/* Arrow */}
                    <div className="shrink-0 flex flex-col items-center" data-id="element-436">
                      <SparklesIcon className="w-8 h-8 text-purple-500 mb-2 animate-pulse" data-id="element-437" />
                      <ArrowRightIcon className="w-6 h-6 text-gray-500 hidden md:block" data-id="element-438" />
                    </div>

                    {/* After */}
                    <div className="flex-1 w-full bg-purple-500/10 rounded-xl p-4 border border-purple-500/30 text-center relative overflow-hidden" data-id="element-439">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent opacity-50" data-id="element-440" />
                      <div className="aspect-square bg-gradient-to-br from-purple-900/50 to-black rounded-lg mb-4 flex items-center justify-center border border-purple-500/20 relative z-10" data-id="element-441">
                        <ImageIcon className="w-12 h-12 text-purple-300" data-id="element-442" />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-wider text-purple-300 relative z-10" data-id="element-443">
                        After (Lifestyle)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section-padding bg-black border-y border-white/5" data-id="element-444">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-445">
          <h2 className="text-4xl font-bold mb-6" data-id="element-446">How It Works</h2>
          <p className="text-xl text-gray-400" data-id="element-447">
            Generate stunning imagery in three simple steps.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-448">
          {[{
          step: '01',
          title: 'Select Your Product',
          desc: 'Choose any product directly from your Shopify catalog.'
        }, {
          step: '02',
          title: 'Describe Your Scene',
          desc: 'Use custom prompts or choose from 20+ pre-set themes.'
        }, {
          step: '03',
          title: 'Generate & Publish',
          desc: 'AI creates stunning lifestyle images. Publish directly to your store.'
        }].map((item, i) => <div key={i} className="glass-card p-8 border-white/10 relative overflow-hidden group" data-id="element-449">
              <div className="absolute top-0 left-0 w-full h-1 bg-purple-500/50 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" data-id="element-450" />
              <div className="text-5xl font-bold text-purple-500/20 mb-6" data-id="element-451">
                {item.step}
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-452">
                {item.title}
              </h3>
              <p className="text-gray-400" data-id="element-453">{item.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Key Features */}
      <section className="section-padding" data-id="element-454">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-455">
          <h2 className="text-4xl font-bold mb-6" data-id="element-456">Key Features</h2>
          <p className="text-xl text-gray-400" data-id="element-457">
            Everything you need to upgrade your product photography.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-458">
          {[{
          icon: <SparklesIcon className="w-6 h-6 text-purple-400" data-id="element-459" />,
          title: 'AI-Powered Generation',
          desc: 'Advanced AI creates realistic lifestyle imagery from your product photos.'
        }, {
          icon: <UploadIcon className="w-6 h-6 text-purple-400" data-id="element-460" />,
          title: 'Direct Publishing',
          desc: 'Publish generated images directly to your Shopify products with one click.'
        }, {
          icon: <PaletteIcon className="w-6 h-6 text-purple-400" data-id="element-461" />,
          title: 'Custom Scenes',
          desc: 'Describe any scene you want or use our 20+ pre-set product categories.'
        }, {
          icon: <ImageIcon className="w-6 h-6 text-purple-400" data-id="element-462" />,
          title: 'Image Gallery',
          desc: 'All generated assets are stored permanently in-app for repeat use.'
        }, {
          icon: <DownloadIcon className="w-6 h-6 text-purple-400" data-id="element-463" />,
          title: 'High Resolution Export',
          desc: 'Export files for product pages, social media, and ad campaigns.'
        }].map((feature, i) => <div key={i} className="glass-card p-8 border-white/10 hover:border-purple-500/30 transition-colors" data-id="element-464">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-6" data-id="element-465">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-466">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-467">
                {feature.desc}
              </p>
            </div>)}
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-468">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-469">
          <h2 className="text-4xl font-bold mb-6" data-id="element-470">Perfect For</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-471">
          {['Product Pages', 'Social Media Content', 'Ad Campaigns', 'Seasonal Promotions'].map((useCase, i) => <div key={i} className="glass-card p-6 border-white/10 flex items-center gap-4" data-id="element-472">
              <CheckCircle2Icon className="w-6 h-6 text-purple-500 shrink-0" data-id="element-473" />
              <span className="text-lg font-medium text-white" data-id="element-474">{useCase}</span>
            </div>)}
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding" data-id="element-475">
        <div className="max-w-md mx-auto" data-id="element-476">
          <div className="glass-card p-10 border-purple-500/30 bg-gradient-to-b from-purple-900/10 to-transparent text-center relative overflow-hidden" data-id="element-477">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-purple-400 to-purple-600" data-id="element-478" />
            <h3 className="text-3xl font-bold text-white mb-4" data-id="element-479">
              Free Plan Available
            </h3>
            <p className="text-gray-400 mb-8" data-id="element-480">
              Get started with our free plan. No credit card required.
            </p>

            <ul className="space-y-4 mb-10 text-left" data-id="element-481">
              {['AI image generation', 'Product catalog integration', 'Permanent image gallery', 'Direct publishing to store'].map((f, i) => <li key={i} className="flex items-center gap-3 text-gray-300" data-id="element-482">
                  <CheckCircle2Icon className="w-5 h-5 text-purple-400 shrink-0" data-id="element-483" />{' '}
                  {f}
                </li>)}
            </ul>

            <a href="https://apps.shopify.com/ai-product-photoshoot-dfd" target="_blank" rel="noopener noreferrer" className="block w-full py-4 rounded-full font-bold bg-white text-black hover:bg-gray-200 transition-colors" data-id="element-484">
              Install Free on Shopify
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-purple-900/20" data-id="element-485">
        <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-486">
          Ready to transform your product photography?
        </h2>
        <a href="https://apps.shopify.com/ai-product-photoshoot-dfd" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-purple-600 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-purple-500 transition-colors shadow-[0_0_30px_rgba(147,51,234,0.3)] hover:scale-105 transform duration-300" data-id="element-487">
          Install AI Product Photoshoot - DFD{' '}
          <ExternalLinkIcon className="w-5 h-5" data-id="element-488" />
        </a>

        {/* App Details Footer */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap justify-center gap-8 text-sm text-gray-500 max-w-3xl mx-auto" data-id="element-489">
          <p data-id="element-490">
            Developer: <span className="text-gray-300" data-id="element-491">Pankaj Maurya</span>
          </p>
          <p data-id="element-492">
            Works with: <span className="text-gray-300" data-id="element-493">Shopify Admin</span>
          </p>
          <p data-id="element-494">
            Language: <span className="text-gray-300" data-id="element-495">English</span>
          </p>
        </div>
      </section>
    </div>;
}